import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../lib/supabase';

export interface Recipient {
  email: string;
  name?: string | null;
  avatar?: string | null;
}

interface MailRecipientInputProps {
  label: string;
  recipients: Recipient[];
  onChange: (r: Recipient[]) => void;
  autoFocus?: boolean;
  trailing?: React.ReactNode; // es. pulsante Cc/Bcc
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

async function searchContacts(q: string): Promise<Recipient[]> {
  const esc = q.replace(/[%_]/g, '\\$&');
  const [clientsRes, contactsRes] = await Promise.all([
    supabase.from('clients').select('business_name, email, logo_url').not('email', 'is', null).or(`business_name.ilike.%${esc}%,email.ilike.%${esc}%`).limit(6),
    supabase.from('client_contacts').select('name, email').not('email', 'is', null).or(`name.ilike.%${esc}%,email.ilike.%${esc}%`).limit(6),
  ]);
  const out: Recipient[] = [];
  for (const c of (clientsRes.data || [])) out.push({ name: (c as any).business_name, email: (c as any).email, avatar: (c as any).logo_url });
  for (const c of (contactsRes.data || [])) out.push({ name: (c as any).name, email: (c as any).email });
  const seen = new Set<string>();
  return out.filter(r => {
    const k = (r.email || '').toLowerCase();
    if (!k || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

export function MailRecipientInput({ label, recipients, onChange, autoFocus, trailing }: MailRecipientInputProps) {
  const [value, setValue] = useState('');
  const [suggestions, setSuggestions] = useState<Recipient[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onDown = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  useEffect(() => {
    const q = value.trim();
    if (q.length < 2) { setSuggestions([]); setOpen(false); return; }
    const h = setTimeout(async () => {
      const existing = new Set(recipients.map(r => r.email.toLowerCase()));
      const res = (await searchContacts(q)).filter(r => !existing.has(r.email.toLowerCase()));
      setSuggestions(res);
      setActive(0);
      setOpen(res.length > 0);
    }, 250);
    return () => clearTimeout(h);
  }, [value, recipients]);

  const add = (r: Recipient) => {
    const email = r.email.trim();
    if (!email) return;
    if (recipients.some(x => x.email.toLowerCase() === email.toLowerCase())) { setValue(''); return; }
    onChange([...recipients, { email, name: r.name, avatar: r.avatar }]);
    setValue('');
    setSuggestions([]);
    setOpen(false);
    inputRef.current?.focus();
  };

  const commitTyped = () => {
    const v = value.trim().replace(/,$/, '').trim();
    if (v && EMAIL_RE.test(v)) add({ email: v });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (open && suggestions.length > 0 && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      setActive(a => e.key === 'ArrowDown' ? Math.min(a + 1, suggestions.length - 1) : Math.max(a - 1, 0));
      return;
    }
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      if (open && suggestions[active]) { e.preventDefault(); add(suggestions[active]); return; }
      if (value.trim()) { e.preventDefault(); commitTyped(); return; }
    }
    if (e.key === 'Backspace' && !value && recipients.length > 0) {
      onChange(recipients.slice(0, -1));
    }
  };

  const initial = (r: Recipient) => (r.name || r.email || '?').charAt(0).toUpperCase();

  return (
    <div ref={wrapRef} className="relative flex items-center border-b border-[rgba(55,53,47,0.06)] dark:border-white/[0.06] px-4">
      <label className="py-2 text-xs font-medium text-[#787774] dark:text-white/40 w-12 flex-shrink-0 self-start mt-0.5">{label}</label>
      <div className="flex-1 flex flex-wrap items-center gap-1 py-1.5 min-w-0">
        {recipients.map((r, i) => (
          <span key={`${r.email}-${i}`} className="inline-flex items-center gap-1.5 pl-1 pr-1.5 py-0.5 rounded-full bg-[rgba(55,53,47,0.06)] dark:bg-white/10 text-xs text-[#37352F] dark:text-white max-w-[220px]">
            {r.avatar ? (
              <img src={r.avatar} alt="" className="w-4 h-4 rounded-full object-cover" />
            ) : (
              <span className="w-4 h-4 rounded-full bg-[#2383E2]/15 text-[#2383E2] flex items-center justify-center text-[9px] font-semibold">{initial(r)}</span>
            )}
            <span className="truncate">{r.name || r.email}</span>
            <button onClick={() => onChange(recipients.filter((_, j) => j !== i))} className="text-[#787774] hover:text-red-500 flex-shrink-0"><X className="h-3 w-3" /></button>
          </span>
        ))}
        <input
          ref={inputRef}
          autoFocus={autoFocus}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={commitTyped}
          className="flex-1 min-w-[120px] py-0.5 text-sm bg-transparent border-none focus:outline-none text-[#37352F] dark:text-white"
          placeholder={recipients.length === 0 ? 'Aggiungi destinatari…' : ''}
        />
      </div>
      {trailing}

      {/* Suggerimenti */}
      {open && suggestions.length > 0 && (
        <div className="absolute top-full left-12 right-4 mt-0.5 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-[rgba(55,53,47,0.12)] dark:border-gray-700 py-1 z-30 max-h-64 overflow-y-auto">
          {suggestions.map((s, i) => (
            <button
              key={`${s.email}-${i}`}
              onMouseDown={(e) => { e.preventDefault(); add(s); }}
              onMouseEnter={() => setActive(i)}
              className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left ${i === active ? 'bg-[rgba(55,53,47,0.05)] dark:bg-white/5' : ''}`}
            >
              {s.avatar ? (
                <img src={s.avatar} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
              ) : (
                <span className="w-6 h-6 rounded-full bg-[#2383E2]/15 text-[#2383E2] flex items-center justify-center text-[10px] font-semibold flex-shrink-0">{initial(s)}</span>
              )}
              <div className="min-w-0">
                {s.name && <p className="text-sm text-[#37352F] dark:text-white truncate">{s.name}</p>}
                <p className={`truncate ${s.name ? 'text-xs text-[#787774] dark:text-white/40' : 'text-sm text-[#37352F] dark:text-white'}`}>{s.email}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
